import { Sparkles, Edit3 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

export type BaseballTrendActionMode = "default" | "custom";

interface BaseballTrendActionPickerProps {
  mode: BaseballTrendActionMode;
  customAction: string;
  onModeChange: (mode: BaseballTrendActionMode) => void;
  onCustomActionChange: (value: string) => void;
}

/** Step 3 picker — choose between the recommended default motion and a
 *  user-described motion. Only when `custom` is selected does the textarea
 *  expand into view. Mirrors `ProductCreativeStylePicker`. */
export function BaseballTrendActionPicker({
  mode,
  customAction,
  onModeChange,
  onCustomActionChange,
}: BaseballTrendActionPickerProps) {
  const { t, language } = useLanguage();

  return (
    <div className="space-y-4 md:space-y-5">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.baseballTrend.actionTitle}
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
          {t.baseballTrend.actionHint}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <ModeCard
          icon={Sparkles}
          title={t.baseballTrend.actionDefaultTitle}
          subtitle={t.baseballTrend.actionDefaultSubtitle}
          recommended={t.baseballTrend.actionRecommendedBadge}
          isActive={mode === "default"}
          onClick={() => onModeChange("default")}
        />
        <ModeCard
          icon={Edit3}
          title={t.baseballTrend.actionCustomTitle}
          subtitle={t.baseballTrend.actionCustomSubtitle}
          isActive={mode === "custom"}
          onClick={() => onModeChange("custom")}
        />
      </div>

      {mode === "custom" && (
        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
          <textarea
            value={customAction}
            onChange={(e) => onCustomActionChange(e.target.value)}
            placeholder={t.baseballTrend.actionPlaceholder}
            dir={language === "he" ? "rtl" : "ltr"}
            rows={3}
            maxLength={1000}
            className="w-full bg-card/90 backdrop-blur-sm border-2 border-border/60 rounded-2xl p-4 text-sm md:text-base text-foreground placeholder:text-muted-foreground/55 resize-none focus:outline-none focus:border-brand focus:bg-card focus:shadow-[0_0_25px_hsl(var(--brand)/0.18)] hover:border-brand/40 transition-all duration-200"
          />
          <p className="mt-1.5 text-[11px] text-muted-foreground/80 text-end">
            {customAction.length} / 1000
          </p>
        </div>
      )}
    </div>
  );
}

function ModeCard({
  icon: Icon,
  title,
  subtitle,
  recommended,
  isActive,
  onClick,
}: {
  icon: typeof Sparkles;
  title: string;
  subtitle: string;
  recommended?: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        "group relative flex flex-col items-start gap-2 p-4 md:p-5 rounded-2xl border text-start transition-all duration-200",
        isActive
          ? "bg-gradient-to-br from-primary/15 to-primary/5 border-primary shadow-md ring-2 ring-primary/30"
          : "bg-card border-border hover:border-primary/40 hover:shadow-sm md:hover:-translate-y-0.5",
      )}
    >
      {recommended && (
        <span className="absolute top-2 end-2 px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 text-[10px] font-bold uppercase tracking-wider ring-1 ring-emerald-500/30">
          {recommended}
        </span>
      )}
      <div
        className={cn(
          "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
          isActive ? "bg-primary text-white" : "bg-primary/10 text-primary",
        )}
      >
        <Icon className="w-5 h-5" strokeWidth={2.25} />
      </div>
      <span className="text-sm md:text-base font-semibold text-foreground">
        {title}
      </span>
      <span className="text-xs md:text-sm text-muted-foreground leading-snug">
        {subtitle}
      </span>
    </button>
  );
}
